'use client';

import { useEffect, useMemo, useState } from 'react';
import { computePregnancy } from '@/lib/browser-healthfin-tools';

export default function BrowserPregnancyCalculator() {
  const [lmp, setLmp] = useState('');
  const [cycleLength, setCycleLength] = useState('28');
  const [today, setToday] = useState<Date | null>(null);

  // Read the current date only on the client to keep SSR output stable.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setToday(new Date());
  }, []);

  const result = useMemo(
    () => (today ? computePregnancy(lmp, cycleLength, today) : null),
    [lmp, cycleLength, today],
  );

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Pregnancy</span>
        <h2 className="tool-live-title">Pregnancy Due Date Calculator</h2>
        <p className="tool-live-subtitle">
          Enter the first day of your last period to estimate your due date, current week, and key
          milestones. Everything is calculated locally in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>First day of last menstrual period</span>
              <input onChange={(event) => setLmp(event.target.value)} type="date" value={lmp} />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Average cycle length (days)</span>
              <input
                inputMode="numeric"
                onChange={(event) => setCycleLength(event.target.value)}
                placeholder="28"
                type="text"
                value={cycleLength}
              />
            </label>
          </div>
          <p className="tool-live-panel-note">
            This is an estimate for general information only and is not medical advice. Confirm
            dates with your doctor or midwife.
          </p>
        </section>

        <section className="tool-live-preview">
          {!result ? (
            <p className="tool-live-panel-note">Loading…</p>
          ) : !result.ok ? (
            <p className="status-message" style={{ color: '#b91c1c' }}>
              {result.message}
            </p>
          ) : (
            <>
              <div className="tool-html-meta-grid">
                <div className="tool-live-field">
                  <span>Estimated due date</span>
                  <input readOnly type="text" value={result.dueDate ?? ''} />
                </div>
                <div className="tool-live-field">
                  <span>Current gestational age</span>
                  <input readOnly type="text" value={result.gestationalAge ?? ''} />
                </div>
                <div className="tool-live-field">
                  <span>Trimester</span>
                  <input readOnly type="text" value={result.trimester ?? ''} />
                </div>
                <div className="tool-live-field">
                  <span>Progress</span>
                  <input readOnly type="text" value={result.progress ?? ''} />
                </div>
              </div>
              <div style={{ marginTop: '1rem', display: 'grid', gap: '0.5rem' }}>
                {(result.milestones ?? []).map((milestone) => (
                  <div
                    key={milestone.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      fontSize: '0.9rem',
                      borderBottom: '1px solid rgba(148, 163, 184, 0.25)',
                      paddingBottom: '0.4rem',
                    }}
                  >
                    <span style={{ opacity: 0.8 }}>
                      {milestone.label}
                      <span style={{ opacity: 0.6 }}> · {milestone.detail}</span>
                    </span>
                    <strong style={{ textAlign: 'right' }}>{milestone.date}</strong>
                  </div>
                ))}
              </div>
              <p className="tool-live-panel-note">
                Based on Naegele&rsquo;s rule (280 days from the last period), adjusted for your
                cycle length.
              </p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
